/**
 * Validador de CPF com regex e algoritmo de validação
 */

// Regex para formato de CPF (XXX.XXX.XXX-XX ou XXXXXXXXXXX)
export const CPF_REGEX = /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2})$/;

// Regex para apenas números
export const CPF_NUMBERS_ONLY = /^\d{11}$/;

/**
 * Remove caracteres especiais do CPF (pontos e traços)
 * @param {string} cpf - CPF com ou sem formatação
 * @returns {string} - CPF apenas com números
 */
export const cleanCPF = (cpf) => {
  return cpf.replace(/[^\d]/g, "");
};

/**
 * Formata CPF no padrão XXX.XXX.XXX-XX
 * @param {string} cpf - CPF apenas com números
 * @returns {string} - CPF formatado
 */
export const formatCPF = (cpf) => {
  const cleaned = cleanCPF(cpf);
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

/**
 * Valida se o CPF tem 11 dígitos e não é uma sequência repetida
 * @param {string} cpf - CPF apenas com números
 * @returns {boolean} - true se válido, false se inválido
 */
export const validateCPFStructure = (cpf) => {
  // Verifica se tem 11 dígitos
  if (!CPF_NUMBERS_ONLY.test(cpf)) {
    return false;
  }

  // Verifica se não é uma sequência repetida (ex: 111.111.111-11)
  if (/^(\d)\1{10}$/.test(cpf)) {
    return false;
  }

  return true;
};

/**
 * Calcula o dígito verificador do CPF
 * @param {string} cpf - CPF parcial (9 ou 10 dígitos)
 * @param {number} position - Posição do dígito (1 ou 2)
 * @returns {number} - Dígito verificador calculado
 */
export const calculateDigit = (cpf, position) => {
  let sum = 0;
  const start = position === 1 ? 0 : 1;
  const end = position === 1 ? 9 : 10;

  for (let i = start; i < end; i++) {
    sum += parseInt(cpf[i]) * (end - i + 1);
  }

  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

/**
 * Valida o CPF usando o algoritmo oficial
 * @param {string} cpf - CPF completo (11 dígitos)
 * @returns {boolean} - true se válido, false se inválido
 */
export const validateCPFAlgorithm = (cpf) => {
  if (!validateCPFStructure(cpf)) {
    return false;
  }

  // Calcula o primeiro dígito verificador
  const digit1 = calculateDigit(cpf, 1);
  if (parseInt(cpf[9]) !== digit1) {
    return false;
  }

  // Calcula o segundo dígito verificador
  const digit2 = calculateDigit(cpf, 2);
  if (parseInt(cpf[10]) !== digit2) {
    return false;
  }

  return true;
};

/**
 * Validação completa do CPF
 * @param {string} cpf - CPF com ou sem formatação
 * @returns {object} - Objeto com resultado da validação
 */
export const validateCPF = (cpf) => {
  const cleaned = cleanCPF(cpf);

  // Validação básica com regex
  if (!CPF_REGEX.test(cpf) && !CPF_NUMBERS_ONLY.test(cleaned)) {
    return {
      isValid: false,
      error: "Formato de CPF inválido",
      cleaned: cleaned,
      formatted: "",
    };
  }

  // Validação do algoritmo
  if (!validateCPFAlgorithm(cleaned)) {
    return {
      isValid: false,
      error: "CPF inválido",
      cleaned: cleaned,
      formatted: "",
    };
  }

  return {
    isValid: true,
    error: null,
    cleaned: cleaned,
    formatted: formatCPF(cleaned),
  };
};

/**
 * Hook para validação em tempo real (para usar em formulários)
 * @param {string} cpf - CPF digitado
 * @returns {object} - Estado da validação
 */
export const useCPFValidation = (cpf) => {
  if (!cpf || cpf.length === 0) {
    return {
      isValid: null,
      error: null,
      cleaned: "",
      formatted: "",
      isComplete: false,
    };
  }

  const validation = validateCPF(cpf);

  return {
    ...validation,
    isComplete: cpf.length >= 11,
  };
};

/**
 * Máscara para input de CPF (formata automaticamente)
 * @param {string} value - Valor digitado
 * @returns {string} - Valor formatado
 */
export const maskCPF = (value) => {
  const cleaned = cleanCPF(value);

  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
  } else if (cleaned.length <= 9) {
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
  } else {
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(
      6,
      9
    )}-${cleaned.slice(9, 11)}`;
  }
};
